import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

export type OppslagImportance = 'normal' | 'viktig' | 'krever_bekreftelse';

export interface PostSummary {
  id: string;
  title: string;
  ingress: string;
  category: string;
  importance: OppslagImportance;
  authorRole: string;
  publishAtUtc: string;
  commentCount: number;
  reactionCount: number;
  /** When the viewer explicitly confirmed a «krever bekreftelse» notice (null/undefined = not yet). */
  confirmedAtUtc?: string | null;
}

export interface CommentView {
  id: string;
  authorId: string;
  body: string;
  createdAtUtc: string;
}

export interface PostDetail {
  id: string;
  title: string;
  body: string;
  category: string;
  importance: OppslagImportance;
  authorRole: string;
  publishAtUtc: string;
  commentsEnabled: boolean;
  requiresConfirmation: boolean;
  reactionCount: number;
  commentCount: number;
  attachments: string[];
  comments: CommentView[];
}

export interface ReadStats {
  confirmed: number;
  eligible: number;
}

/**
 * Oppslag (notice board) client (`/api/oppslag`, M2). Hand-written to mirror the backend
 * contract. Reads are visibility-filtered server-side (a resident only receives notices targeted
 * to them); "krever lest-bekreftelse" notices expose a confirm action.
 */
@Injectable({ providedIn: 'root' })
export class OppslagService {
  private readonly http = inject(HttpClient);

  get(id: string): Observable<PostDetail> {
    return this.http.get<PostDetail>(`/api/oppslag/${encodeURIComponent(id)}`);
  }

  react(id: string): Observable<void> {
    return this.http.post<void>(`/api/oppslag/${encodeURIComponent(id)}/reactions`, {});
  }

  comment(id: string, body: string): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`/api/oppslag/${encodeURIComponent(id)}/comments`, { body });
  }

  /** Records a passive "seen". */
  markRead(id: string): Observable<void> {
    return this.http.post<void>(`/api/oppslag/${encodeURIComponent(id)}/read`, {});
  }

  /** Records an explicit "krever lest-bekreftelse" confirmation. */
  confirmRead(id: string): Observable<void> {
    return this.http.post<void>(`/api/oppslag/${encodeURIComponent(id)}/confirm-read`, {});
  }

  /** Styre-only read statistics ("X av Y har lest"). */
  readStats(id: string): Observable<ReadStats> {
    return this.http.get<ReadStats>(`/api/oppslag/${encodeURIComponent(id)}/read-stats`);
  }
}
